import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useCrawlFb, useGetCrawlStatus } from '../../../hooks/useCrawlFb';
import { Cookie } from '../../../api/crawlFb';
import CrawlStatusDisplay from './components/CrawlStatusDisplay';
import ImagePreviewModal from './components/ImagePreviewModal';

const CrawlFb = () => {
  const [detectedCookies, setDetectedCookies] = useState<Cookie[]>([]);
  const [fbUrl, setFbUrl] = useState('');
  const [activeTab, setActiveTab] = useState('paste');
  const [crawlId, setCrawlId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<'crawl' | 'status'>('crawl');
  const [cookieTextArea, setCookieTextArea] = useState('');
  const [selectedImage, setSelectedImage] = useState<{
    imageUrl: string;
    title: string;
    images?: { url: string; title?: string }[];
    currentIndex?: number;
  } | null>(null);

  const { mutateAsync: crawlFacebook, isPending: loading } = useCrawlFb();

  const { data: crawlStatus, isLoading: isPolling } = useGetCrawlStatus(
    crawlId,
    !!crawlId
  );

  // Helper functions for localStorage
  const saveCookiesToLocal = (cookies: Cookie[]) => {
    try {
      localStorage.setItem('facebook_cookies', JSON.stringify(cookies));
      localStorage.setItem('facebook_cookies_timestamp', Date.now().toString());
    } catch (error) {
      console.error('Error saving cookies to localStorage:', error);
    }
  };

  const loadCookiesFromLocal = (): Cookie[] | null => {
    try {
      const savedCookies = localStorage.getItem('facebook_cookies');
      const timestamp = localStorage.getItem('facebook_cookies_timestamp');

      if (!savedCookies || !timestamp) return null;

      // Check if cookies are older than 7 days
      const cookieAge = Date.now() - parseInt(timestamp);
      const maxAge = 90 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

      if (cookieAge > maxAge) {
        // Remove expired cookies
        clearSavedCookies();
        return null;
      }

      return JSON.parse(savedCookies);
    } catch (error) {
      console.error('Error loading cookies from localStorage:', error);
      return null;
    }
  };

  const clearSavedCookies = () => {
    try {
      localStorage.removeItem('facebook_cookies');
      localStorage.removeItem('facebook_cookies_timestamp');
      toast.success('Đã xóa cookies đã lưu');
    } catch (error) {
      console.error('Error clearing saved cookies:', error);
    }
  };

  // Load saved cookies and check for Facebook cookies on mount
  useEffect(() => {
    // Load saved cookies first
    const savedCookies = loadCookiesFromLocal();
    if (savedCookies && savedCookies.length > 0) {
      setDetectedCookies(savedCookies);
      // Auto-fill textarea with saved cookies
      setCookieTextArea(JSON.stringify(savedCookies, null, 2));
      // toast.info(`Đã tải ${savedCookies.length} cookies đã lưu từ lần trước`);
    }

    // Then check for current Facebook cookies
    checkForFacebookCookies();
  }, []);

  const checkForFacebookCookies = () => {
    try {
      // Kiểm tra nếu có thể đọc cookies (chỉ hoạt động trên cùng domain)
      const allCookies = document.cookie;
      if (allCookies.includes('facebook') || allCookies.includes('fb')) {
        toast.info('Phát hiện có cookies Facebook trong browser');
      }
    } catch (error) {
      console.log('Không thể tự động đọc cookies:', error);
    }
  };

  const parseCookiesFromJson = (jsonString: string): Cookie[] => {
    try {
      const parsed = JSON.parse(jsonString);
      if (Array.isArray(parsed)) {
        return parsed;
      } else if (parsed.cookies && Array.isArray(parsed.cookies)) {
        return parsed.cookies;
      }
      return [];
    } catch {
      throw new Error('JSON không hợp lệ');
    }
  };

  const handlePasteCookies = (value: string) => {
    setCookieTextArea(value);
    try {
      const cookies = parseCookiesFromJson(value);
      if (cookies.length > 0) {
        setDetectedCookies(cookies);
        // Automatically save cookies when pasted
        saveCookiesToLocal(cookies);
        toast.success(`Đã paste và lưu ${cookies.length} cookies`);
      }
    } catch {
      toast.error('JSON cookies không hợp lệ');
      setDetectedCookies([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!fbUrl) {
      toast.error('Vui lòng nhập URL Facebook');
      return;
    }

    if (detectedCookies.length === 0) {
      toast.error('Vui lòng cung cấp cookies');
      return;
    }

    try {
      const result = await crawlFacebook({
        url: fbUrl,
        cookies: detectedCookies,
      });

      // Save cookies to localStorage when crawl starts successfully
      saveCookiesToLocal(detectedCookies);
      toast.success('Đã lưu cookies để sử dụng lần sau');

      // Start polling with the returned crawl ID and switch to status tab
      setCrawlId(result.id);
      setCurrentStep('status');
    } catch (error) {
      console.error('Error starting crawl:', error);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Đã copy vào clipboard');
  };

  const instructionSteps = [
    'Mở Facebook trong Chrome/Edge/Firefox',
    'Nhấn F12 hoặc chuột phải → Inspect Element',
    'Vào tab Application (Chrome) hoặc Storage (Firefox)',
    'Chọn Cookies → facebook.com',
    'Tìm và copy các cookies quan trọng: c_user, xs, fr, sb, datr',
    'Tạo JSON array với format đúng và paste vào form bên dưới',
  ];

  const extensionSteps = [
    'Cài đặt extension "Facebook Cookies Extractor"',
    'Mở Facebook và đăng nhập tài khoản của bạn',
    'Click vào icon extension trên thanh công cụ browser',
    'Nhấn nút "🔍 Lấy Facebook Cookies"',
    'Nhấn nút "📋 Copy Cookies" để copy cookies vào clipboard',
    'Paste cookies vào form bên dưới',
  ];

  const handleNewCrawl = () => {
    setCrawlId(null);
    setCurrentStep('crawl');
    setFbUrl('');
    setDetectedCookies([]);
    setCookieTextArea('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="max-w-6xl mx-auto p-6 h-full overflow-auto">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-8 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-100/30 to-transparent rounded-full -translate-y-48 translate-x-48"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-gradient-to-tr from-purple-100/30 to-transparent rounded-full translate-y-36 -translate-x-36"></div>
          <div className="relative z-10 flex items-center justify-center mb-8">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full animate-pulse"></div>
                <div className="relative bg-white p-3 rounded-full shadow-lg">
                  <svg
                    className="w-8 h-8 text-blue-600"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </div>
              </div>
              <div className="text-center">
                <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
                  Facebook Crawl System
                </h1>
                <p className="text-gray-600 mt-1">
                  Advanced data extraction platform
                </p>
              </div>
            </div>
          </div>

          {/* Step Tabs */}
          <div className="relative z-10 mb-8">
            <div className="bg-gray-50/50 backdrop-blur-sm p-2 rounded-xl border border-gray-200/50">
              <nav className="flex space-x-2">
                <button
                  onClick={() => setCurrentStep('crawl')}
                  className={`relative py-3 px-6 font-medium text-sm flex items-center rounded-lg transition-all duration-300 ${
                    currentStep === 'crawl'
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg shadow-blue-500/25 transform scale-105'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-white/70'
                  }`}
                  disabled={loading}
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
                    ></path>
                  </svg>
                  Step 1: Facebook Crawl with Cookies
                </button>
                <button
                  onClick={() => setCurrentStep('status')}
                  className={`relative py-3 px-6 font-medium text-sm flex items-center rounded-lg transition-all duration-300 ${
                    currentStep === 'status'
                      ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-lg shadow-purple-500/25 transform scale-105'
                      : 'text-gray-600 hover:text-purple-600 hover:bg-white/70'
                  }`}
                  disabled={!crawlId}
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    ></path>
                  </svg>
                  Step 2: Crawl Status & Results
                  {crawlId && (
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                      Active
                    </span>
                  )}
                </button>
              </nav>
            </div>
          </div>

          {/* Step 1: Crawl Form */}
          {currentStep === 'crawl' && (
            <>
              <div className="relative z-10 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/50 rounded-xl p-6 mb-6 shadow-lg shadow-blue-500/10 backdrop-blur-sm">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className="absolute inset-0 bg-blue-500 rounded-full animate-pulse opacity-75"></div>
                      <div className="relative bg-white p-2 rounded-full shadow-lg">
                        <svg
                          className="w-5 h-5 text-blue-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-blue-900 font-semibold text-lg">
                      🍪 Hướng dẫn lấy cookies
                    </h3>
                    <p className="text-blue-700 text-sm mt-1 leading-relaxed">
                      Bạn có thể sử dụng nhiều cách để cung cấp cookies
                      Facebook. Cookies sẽ được lưu tự động để sử dụng lần sau
                      (tối đa 90 ngày).
                    </p>
                  </div>
                </div>
              </div>

              {/* Saved cookies info */}
              {loadCookiesFromLocal() && detectedCookies.length > 0 && (
                <div className="relative z-10 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200/50 rounded-xl p-6 mb-6 shadow-lg shadow-green-500/10 backdrop-blur-sm animate-fadeIn">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="relative">
                        <div className="absolute inset-0 bg-green-500 rounded-full animate-pulse opacity-75"></div>
                        <div className="relative bg-white p-2 rounded-full shadow-lg">
                          <svg
                            className="w-5 h-5 text-green-500"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-green-900 font-semibold text-lg">
                        ✅ Cookies đã được tải từ lần trước
                      </h3>
                      <p className="text-green-700 text-sm mt-1 leading-relaxed">
                        Cookies Facebook đã được tự động tải từ localStorage.
                        Bạn có thể trực tiếp crawl hoặc thay đổi cookies nếu
                        cần.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="relative z-10 space-y-8">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-800 mb-3">
                    🌐 URL Facebook cần crawl *
                  </label>
                  <div className="relative group">
                    <input
                      type="url"
                      placeholder="https://www.facebook.com/zuck"
                      value={fbUrl}
                      onChange={(e) => setFbUrl(e.target.value)}
                      className="w-full px-4 py-4 pl-12 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 shadow-lg hover:shadow-xl"
                      required
                    />
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                      <div className="relative">
                        <div className="absolute inset-0 bg-blue-500 rounded-full animate-pulse opacity-50"></div>
                        <div className="relative bg-white p-1 rounded-full">
                          <svg
                            className="w-5 h-5 text-blue-500"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-300/30 to-transparent h-px top-1/2 transform -translate-y-1/2"></div>
                  <h3 className="text-xl font-bold text-center bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent relative bg-white px-6 mx-auto w-fit">
                    🍪 Cách lấy Cookies
                  </h3>
                </div>

                {/* Tabs */}
                <div className="bg-white/50 backdrop-blur-sm rounded-xl p-2 border border-gray-200/50 shadow-lg">
                  <nav className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => setActiveTab('paste')}
                      className={`py-3 px-6 font-semibold text-sm rounded-lg transition-all duration-300 transform ${
                        activeTab === 'paste'
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg shadow-blue-500/25 scale-105'
                          : 'text-gray-600 hover:text-blue-600 hover:bg-white/70 hover:scale-102'
                      }`}
                    >
                      📋 Paste JSON
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('extension')}
                      className={`py-3 px-6 font-semibold text-sm rounded-lg transition-all duration-300 transform ${
                        activeTab === 'extension'
                          ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/25 scale-105'
                          : 'text-gray-600 hover:text-green-600 hover:bg-white/70 hover:scale-102'
                      }`}
                    >
                      🧩 Extension (Khuyến nghị)
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('guide')}
                      className={`py-3 px-6 font-semibold text-sm rounded-lg transition-all duration-300 transform ${
                        activeTab === 'guide'
                          ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-lg shadow-purple-500/25 scale-105'
                          : 'text-gray-600 hover:text-purple-600 hover:bg-white/70 hover:scale-102'
                      }`}
                    >
                      📖 Hướng dẫn thủ công
                    </button>
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="mt-6">
                  {activeTab === 'paste' && (
                    <div className="space-y-4">
                      <p className="text-gray-600">
                        Paste cookies dưới dạng JSON array:
                      </p>
                      <textarea
                        rows={8}
                        placeholder='[{"name": "c_user", "value": "...", "domain": ".facebook.com", ...}]'
                        value={cookieTextArea}
                        onChange={(e) => handlePasteCookies(e.target.value)}
                        className="w-full px-4 py-4 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 shadow-lg hover:shadow-xl font-mono text-sm resize-none"
                      />
                    </div>
                  )}

                  {activeTab === 'extension' && (
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <svg
                          className="w-5 h-5 text-green-500 mr-2"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <h4 className="text-lg font-medium text-gray-800">
                          Sử dụng Facebook Cookies Extractor (Dễ nhất)
                        </h4>
                      </div>
                      <ol className="list-decimal list-inside space-y-2 text-gray-600">
                        {extensionSteps.map((step, index) => (
                          <li key={index}>{step}</li>
                        ))}
                      </ol>

                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <svg
                            className="w-5 h-5 text-green-500 mt-0.5 mr-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <div>
                            <h5 className="text-green-800 font-medium">
                              Cài đặt Extension
                            </h5>
                            <p className="text-green-700 text-sm mb-2">
                              Vào Chrome → Cài đặt → Extensions → Developer mode
                              → Load unpacked
                            </p>
                            <p className="text-green-700 text-sm">
                              Chọn thư mục:{' '}
                              <code className="bg-green-100 px-1 rounded">
                                facebook-cookies-extension
                              </code>
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <svg
                            className="w-5 h-5 text-blue-500 mt-0.5 mr-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <div>
                            <h5 className="text-blue-800 font-medium">
                              Lưu ý quan trọng
                            </h5>
                            <p className="text-blue-700 text-sm">
                              Extension chỉ lấy các cookies quan trọng: c_user,
                              xs, fr, sb, datr. Đảm bảo bạn đã đăng nhập
                              Facebook trước khi sử dụng.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'guide' && (
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <svg
                          className="w-5 h-5 text-blue-500 mr-2"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <h4 className="text-lg font-medium text-gray-800">
                          Cách lấy cookies từ Browser DevTools
                        </h4>
                      </div>
                      <ol className="list-decimal list-inside space-y-2 text-gray-600">
                        {instructionSteps.map((step, index) => (
                          <li key={index}>{step}</li>
                        ))}
                      </ol>

                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <svg
                            className="w-5 h-5 text-amber-500 mt-0.5 mr-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <div>
                            <h5 className="text-amber-800 font-medium">
                              Cookies quan trọng cần lấy
                            </h5>
                            <div className="text-amber-700 text-sm space-y-1">
                              <p>
                                <strong>c_user:</strong> User ID
                              </p>
                              <p>
                                <strong>xs:</strong> Authentication token
                              </p>
                              <p>
                                <strong>fr:</strong> Facebook token
                              </p>
                              <p>
                                <strong>sb:</strong> Browser session
                              </p>
                              <p>
                                <strong>datr:</strong> Device authentication
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <svg
                            className="w-5 h-5 text-gray-500 mt-0.5 mr-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <div>
                            <h5 className="text-gray-800 font-medium">
                              Format JSON cần thiết:
                            </h5>
                            <pre className="bg-gray-100 p-2 rounded text-xs mt-2 overflow-x-auto">
                              {`[
  {
    "name": "c_user",
    "value": "100000000000000",
    "domain": ".facebook.com",
    "path": "/",
    "expires": 1234567890,
    "httpOnly": false,
    "secure": true,
    "sameSite": "Lax"
  }
]`}
                            </pre>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {detectedCookies.length > 0 && (
                  <div className="relative z-10 mt-8 animate-fadeIn">
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/50 rounded-xl p-6 shadow-lg shadow-amber-500/10 backdrop-blur-sm">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="relative">
                            <div className="absolute inset-0 bg-amber-500 rounded-full animate-pulse opacity-75"></div>
                            <div className="relative bg-white p-2 rounded-full shadow-lg">
                              <svg
                                className="w-5 h-5 text-amber-500"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          </div>
                          <h4 className="text-xl font-bold text-amber-900">
                            🍪 Cookies đã detect ({detectedCookies.length}{' '}
                            cookies)
                          </h4>
                        </div>
                        <div className="flex items-center space-x-3">
                          {loadCookiesFromLocal() && (
                            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200/50 shadow-sm">
                              💾 Đã lưu
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setDetectedCookies([]);
                              setCookieTextArea('');
                              clearSavedCookies();
                            }}
                            className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-gradient-to-r from-red-100 to-pink-100 text-red-800 border border-red-200/50 shadow-sm hover:from-red-200 hover:to-pink-200 transition-all duration-300 transform hover:scale-105"
                          >
                            🗑️ Xóa cookies
                          </button>
                        </div>
                      </div>
                      <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 max-h-64 overflow-auto border border-amber-200/30 shadow-inner">
                        <div className="space-y-3">
                          {detectedCookies.map((cookie, index) => (
                            <div
                              key={index}
                              className="group flex items-center justify-between p-3 bg-gradient-to-r from-white/80 to-gray-50/80 rounded-lg border border-gray-200/50 hover:from-amber-50/80 hover:to-orange-50/80 hover:border-amber-300/50 transition-all duration-300 shadow-sm hover:shadow-md"
                            >
                              <div className="flex items-center space-x-3">
                                <code className="bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 px-3 py-1.5 rounded-lg text-sm font-semibold border border-blue-200/50 shadow-sm">
                                  {cookie.name}
                                </code>
                                <span className="text-gray-700 font-mono text-sm bg-gray-100/50 px-2 py-1 rounded">
                                  {cookie.value.substring(0, 35)}...
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => copyToClipboard(cookie.value)}
                                className="opacity-0 group-hover:opacity-100 inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-105"
                              >
                                📋 Copy
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-10">
                  <button
                    type="submit"
                    disabled={
                      !fbUrl ||
                      detectedCookies.length === 0 ||
                      loading ||
                      !!crawlId
                    }
                    className="group relative w-full flex justify-center py-4 px-6 border border-transparent text-lg font-bold rounded-2xl text-white bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 hover:from-blue-700 hover:via-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-4 focus:ring-blue-500/50 disabled:from-gray-400 disabled:via-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed transform transition-all duration-300 hover:scale-105 hover:shadow-2xl shadow-lg"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-2xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
                    <div className="relative flex items-center">
                      {loading ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-6 w-6 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span className="text-lg">Đang gửi...</span>
                        </>
                      ) : crawlId ? (
                        <span className="text-lg">⏳ Đang xử lý...</span>
                      ) : (
                        <>
                          <span className="text-xl mr-2">🚀</span>
                          <span className="text-lg">Gửi Request Crawl</span>
                        </>
                      )}
                    </div>
                  </button>
                </div>
              </form>
            </>
          )}

          {/* Step 2: Status Display */}
          {currentStep === 'status' && crawlId && (
            <CrawlStatusDisplay
              crawlStatus={crawlStatus}
              isPolling={isPolling}
              onNewCrawl={handleNewCrawl}
              isShowNewCrawl={true}
              onImageSelect={setSelectedImage}
            />
          )}

          {/* No crawl active message for status tab */}
          {currentStep === 'status' && !crawlId && (
            <div className="text-center py-12">
              <svg
                className="w-16 h-16 text-gray-400 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                ></path>
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Active Crawl
              </h3>
              <p className="text-gray-600 mb-4">
                Chưa có crawl nào đang chạy. Hãy bắt đầu crawl ở Step 1.
              </p>
              <button
                onClick={() => setCurrentStep('crawl')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  ></path>
                </svg>
                Bắt đầu Crawl
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={!!selectedImage}
        imageUrl={selectedImage?.imageUrl || null}
        onClose={() => setSelectedImage(null)}
        title={selectedImage?.title || 'Preview'}
        images={selectedImage?.images}
        currentIndex={selectedImage?.currentIndex}
        onImageChange={(index) => {
          if (selectedImage?.images) {
            setSelectedImage({
              ...selectedImage,
              imageUrl: selectedImage.images[index].url,
              currentIndex: index,
              title: selectedImage.images[index].title || selectedImage.title,
            });
          }
        }}
      />
    </div>
  );
};

// Custom styles for animations
const styles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes slideInLeft {
    from { opacity: 0; transform: translateX(-20px); }
    to { opacity: 1; transform: translateX(0); }
  }
  
  @keyframes glow {
    0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.4); }
    50% { box-shadow: 0 0 40px rgba(59, 130, 246, 0.8); }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.6s ease-out;
  }
  
  .animate-slideInLeft {
    animation: slideInLeft 0.6s ease-out;
  }
  
  .animate-glow {
    animation: glow 2s infinite;
  }
`;

// Inject styles
if (
  typeof document !== 'undefined' &&
  !document.querySelector('#crawl-fb-styles')
) {
  const styleElement = document.createElement('style');
  styleElement.id = 'crawl-fb-styles';
  styleElement.textContent = styles;
  document.head.appendChild(styleElement);
}

export default CrawlFb;
